/**
 * Builds the `patient_context` bridge payload sent to the backend with every
 * command (UX-02 slice 2). Extracted verbatim from App.tsx's handleSendMessage
 * IIFE — a pure function of the current patient, digital-twin overview, and the
 * entity lists, so it is trivially testable outside the god component.
 *
 * The shape is a stable contract with the backend (schema_version
 * 'march.patient_context_bridge.v1'); the caps below (6 items/group, 6k-char
 * summary, 30 history items) match the original inline logic exactly.
 */
import type { EntityListItem } from './commandApi';

export interface PatientContextBridgeInputs {
  patientId: string;
  threadId: string;
  currentPatient: { name?: string; status?: string; risk?: string };
  digitalTwinOverview: { summary?: string | null; available?: boolean } | null;
  assessments: EntityListItem[];
  alerts: EntityListItem[];
  redFlags: EntityListItem[];
  visits: EntityListItem[];
  simulations: EntityListItem[];
}

export interface PatientContextBridge {
  schema_version: 'march.patient_context_bridge.v1';
  patient_id: string | null;
  summary: string;
  history_items: Array<Record<string, unknown>>;
  metadata: {
    source: 'frontend_context_bridge';
    thread_id: string;
    patient_name: string | null;
    status: string | null;
    risk: string | null;
    digital_twin_available: boolean;
    entity_counts: {
      assessments: number;
      alerts: number;
      red_flags: number;
      visits: number;
      simulations: number;
    };
  };
}

export function buildPatientContextBridge(input: PatientContextBridgeInputs): PatientContextBridge {
  const {
    patientId,
    threadId,
    currentPatient,
    digitalTwinOverview,
    assessments,
    alerts,
    redFlags,
    visits,
    simulations,
  } = input;

  const lines: string[] = [];
  const historyItems: Array<Record<string, unknown>> = [];

  const addLine = (label: string, value: unknown) => {
    const text = String(value ?? '').trim();
    if (text) lines.push(`${label}: ${text}`);
  };
  const addEntityGroup = (label: string, items: EntityListItem[]) => {
    const compact = (items ?? [])
      .slice(0, 6)
      .map((item) => {
        const title = String(item.title ?? item.type ?? item.id ?? '').trim();
        const summary = String(item.summary ?? '').trim();
        const chips = Array.isArray(item.chips) ? item.chips.slice(0, 6).join(', ') : '';
        return [title, summary, chips].filter(Boolean).join(' — ');
      })
      .filter(Boolean);
    if (compact.length) {
      lines.push(`${label}: ${compact.join(' | ')}`);
      compact.forEach((summary, idx) => {
        historyItems.push({ type: label, id: `${label}-${idx}`, summary });
      });
    }
  };

  addLine('Patient', [currentPatient.name, patientId].filter(Boolean).join(' / '));
  addLine('Status', currentPatient.status);
  addLine('Risk', currentPatient.risk);
  addLine('Digital twin summary', digitalTwinOverview?.summary);
  addEntityGroup('Assessments', assessments);
  addEntityGroup('Alerts', alerts);
  addEntityGroup('Red flags', redFlags);
  addEntityGroup('Recent visits', visits);
  addEntityGroup('Simulations', simulations);

  const summary = lines.join('\n').slice(0, 6000);
  return {
    schema_version: 'march.patient_context_bridge.v1',
    patient_id: patientId || null,
    summary,
    history_items: historyItems.slice(0, 30),
    metadata: {
      source: 'frontend_context_bridge',
      thread_id: threadId,
      patient_name: currentPatient.name || null,
      status: currentPatient.status || null,
      risk: currentPatient.risk || null,
      digital_twin_available: digitalTwinOverview?.available === true,
      entity_counts: {
        assessments: assessments.length,
        alerts: alerts.length,
        red_flags: redFlags.length,
        visits: visits.length,
        simulations: simulations.length,
      },
    },
  };
}
